import React, { useState, useMemo } from 'react';
import { useSettings } from '../contexts/SettingsContext';
import CalendarView from './CalendarView';
import HistoryView from './HistoryView';
import NotesSection from './NotesSection';
import amazonAPI from '../services/amazonAPI';

// ── Design tokens ──────────────────────────────────────────────────────────────
const T = {
  green800:  '#1a4a1c',
  green700:  '#1e5e20',
  green600:  '#2a7a2d',
  green500:  '#3a9a3e',
  green200:  '#b8e0bb',
  green100:  '#dff0e0',
  green50:   '#f0f9f1',
  teal:      '#1ec98a',   // ".Smart" brand accent — used for text only
  amber:     '#c8761a',
  amberLight:'#fef3e2',
  bg:        '#f5f8f5',
  white:     '#ffffff',
  text:      '#1a2e1b',
  textMed:   '#4a6a4c',
  textLight: '#8aaa8c',
  border:    '#e0ece0',
  shadow:    'rgba(30,80,32,0.10)',
};

// Deterministic category colour from section name
const CATEGORY_PALETTE = [
  '#c0392b', // red
  '#2a7a2d', // green600
  '#c8761a', // amber
  '#1a6fa8', // blue
  '#7c3aed', // purple
  '#0e7490', // teal
  '#b45309', // orange
  '#047857', // emerald
];
function categoryColor(title) {
  const hash = title.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

const PRIORITY_STYLE = {
  critical: { bg: '#fdecea', color: '#c0392b', label: 'Critical' },
  high:     { bg: '#fff8e6', color: '#c8761a', label: 'High' },
  medium:   { bg: '#f0f9f1', color: '#1e5e20', label: 'Medium' },
  low:      { bg: '#f5f5f5', color: '#8aaa8c', label: 'Low' },
};

const MONTHS_FULL = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];



// ── Sub-components ─────────────────────────────────────────────────────────────

function PriorityPill({ priority }) {
  const s = PRIORITY_STYLE[priority] || PRIORITY_STYLE.medium;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.04em',
      padding: '2px 7px', borderRadius: 99,
      background: s.bg, color: s.color,
      textTransform: 'uppercase', whiteSpace: 'nowrap', flexShrink: 0,
    }}>
      {s.label}
    </span>
  );
}

function Checkbox({ checked, onChange }) {
  return (
    <div
      onClick={onChange}
      style={{
        width: 20, height: 20, borderRadius: 5,
        border: checked ? 'none' : `2px solid ${T.border}`,
        background: checked ? T.green700 : T.white,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s',
      }}
    >
      {checked && (
        <svg width="11" height="9" viewBox="0 0 12 10">
          <polyline points="1,5 4,9 11,1" stroke="white" strokeWidth="2"
            fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )}
    </div>
  );
}

function ProductCard({ product }) {
  const [imgFailed, setImgFailed] = useState(false);
  return (
    <div style={{ marginBottom: 0 }}>
      <div style={{
        width: '100%', height: 80, background: T.green50,
        borderRadius: 10, marginBottom: 8,
        overflow: 'hidden', display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}>
        {!imgFailed ? (
          <img
            src={`/images/products/${product.asin}.jpg`}
            alt={product.title}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            onError={() => setImgFailed(true)}
          />
        ) : (
          <span style={{ fontSize: 10, color: T.textLight, textAlign: 'center', padding: 8 }}>
            {product.category}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12, fontWeight: 600, color: T.text, lineHeight: 1.3, marginBottom: 4 }}>
        {product.title}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: T.green700 }}>{product.price}</span>
        {product.rating && (
          <span style={{ fontSize: 11, color: '#c8a030' }}>★ {product.rating}</span>
        )}
      </div>
      <button
        onClick={() => window.open(product.affiliateUrl, '_blank', 'noopener,noreferrer')}
        style={{
          width: '100%', background: T.green700, color: 'white',
          border: 'none', borderRadius: 9, padding: '8px',
          fontSize: 12, fontWeight: 700, cursor: 'pointer',
        }}
      >
        View on Amazon
      </button>
    </div>
  );
}

// ── DesktopDashboard ───────────────────────────────────────────────────────────

const DesktopDashboard = ({
  monthName,
  activeYear,
  monthData,
  completedTasks,
  onTaskToggle,
  monthStats,
  onMonthChange,
  onHomeClick,
  onSettingsClick,
  onStoreClick,
  onDismissAlert,
  zoneAlerts = [],
  zoneCustomizedData = {},
}) => {
  const { settings } = useSettings();
  const [filterCat, setFilterCat] = useState('All');
  const [activeNav, setActiveNav] = useState('Dashboard');

  const monthIndex = MONTHS_FULL.indexOf(monthName);
  const season = monthData?.season
    ? monthData.season.charAt(0).toUpperCase() + monthData.season.slice(1)
    : '';
  const seasonEmoji = { spring: '🌷', summer: '☀️', fall: '🍂', winter: '❄️' }[monthData?.season] || '🌿';

  // Dynamic product recommendations — scored against actual tasks + priorities
  const recommendedProducts = useMemo(() => {
    if (!monthData?.sections) return [];
    return amazonAPI.getRecommendedProducts(monthData.sections, monthData.season, 4);
  }, [monthData]);

  // Build task groups from monthData sections
  const taskGroups = useMemo(() => {
    if (!monthData) return [];
    return monthData.sections.map((section, sIdx) => ({
      title: section.title,
      icon:  section.icon,
      color: categoryColor(section.title),
      tasks: section.tasks.map((task, tIdx) => {
        const id = `${activeYear}-${monthName}-${sIdx}-${tIdx}`;
        return {
          id,
          title:    task.text,
          priority: task.priority || 'medium',
          done:     completedTasks.has(id),
        };
      }),
    }));
  }, [monthData, activeYear, monthName, completedTasks]);

  const allCategories  = ['All', ...taskGroups.map(g => g.title)];
  const visibleGroups  = filterCat === 'All' ? taskGroups : taskGroups.filter(g => g.title === filterCat);

  const pct            = monthStats?.percentage || 0;
  const totalTasks     = monthStats?.total       || 0;
  const completedCount = monthStats?.completed   || 0;

  const handleMonthNav = (dir) => {
    const newIdx  = ((monthIndex + dir) + 12) % 12;
    const newMonth = MONTHS_FULL[newIdx];
    let newYear   = activeYear;
    if (dir ===  1 && monthIndex === 11) newYear = activeYear + 1;
    if (dir === -1 && monthIndex ===  0) newYear = activeYear - 1;
    if (onMonthChange) onMonthChange(newMonth, newYear);
  };

  const handleNavClick = (nav) => {
    if (nav === 'Settings') {
      if (onSettingsClick) onSettingsClick();
      return; // modal only — don't change the active view
    }
    setActiveNav(nav);
    if (nav === 'Dashboard' || nav === 'Calendar') onHomeClick();
  };

  return (
    <div style={{
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      background: T.bg, height: '100%',
      display: 'flex', flexDirection: 'column',
    }}>

      {/* ── Top navigation bar ────────────────────────────────────────────── */}
      <div style={{
        background: T.white, borderBottom: `1px solid ${T.border}`,
        padding: '0 32px', display: 'flex', alignItems: 'center',
        gap: 32, height: 60, flexShrink: 0,
      }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <img
            src="/logo192.png"
            alt="Lawn.Smart logo"
            style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0 }}
          />
          <div style={{ lineHeight: 1.15 }}>
            <div style={{ fontSize: 18, letterSpacing: '-0.02em', fontFamily: "'DM Sans', sans-serif" }}>
              <span style={{ fontWeight: 800, color: T.green800 }}>Lawn</span>
              <span style={{ fontWeight: 800, color: T.teal }}>.Smart</span>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 28, width: 1, background: T.border }} />

        {/* Nav links */}
        {['Dashboard', 'Calendar', 'History', 'Settings'].map(nav => (
          <div
            key={nav}
            onClick={() => handleNavClick(nav)}
            style={{
              fontSize: 14, fontWeight: 600, cursor: 'pointer',
              color: activeNav === nav ? T.green700 : T.textLight,
              borderBottom: activeNav === nav ? `2px solid ${T.green700}` : '2px solid transparent',
              paddingBottom: 2, paddingTop: 2,
              transition: 'color 0.15s',
            }}
          >
            {nav}
          </div>
        ))}

        {/* Right side */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{
            background: T.green50, border: `1px solid ${T.green200}`,
            borderRadius: 99, padding: '5px 14px',
            fontSize: 13, color: T.green800, fontWeight: 600, whiteSpace: 'nowrap',
          }}>
            📍 {settings.state} · Zone {settings.hardinessZone}{season ? ` · ${season}` : ''}
          </div>
          <button
            onClick={onStoreClick}
            style={{
              background: T.green50, border: `1px solid ${T.green200}`,
              borderRadius: 10, height: 36,
              display: 'flex', alignItems: 'center', gap: 6, padding: '0 14px',
              cursor: 'pointer',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke={T.green700} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 6h18M16 10a4 4 0 01-8 0" stroke={T.green700} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span style={{ fontSize: 13, fontWeight: 600, color: T.green700 }}>Store</span>
          </button>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Left sidebar ────────────────────────────────────────────────── */}
        <div style={{
          width: 220, background: T.white, borderRight: `1px solid ${T.border}`,
          padding: '24px 20px', display: 'flex', flexDirection: 'column',
          gap: 20, overflowY: 'auto', flexShrink: 0,
        }}>

          {/* Month navigator */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
              color: T.textLight, textTransform: 'uppercase', marginBottom: 10,
            }}>
              Navigate
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <button
                onClick={() => handleMonthNav(-1)}
                style={{
                  background: T.bg, border: `1px solid ${T.border}`,
                  borderRadius: 8, width: 32, height: 32,
                  cursor: 'pointer', fontSize: 16, color: T.textMed,
                }}
              >‹</button>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{monthName}</div>
                <div style={{ fontSize: 11, color: T.green700, fontWeight: 600 }}>
                  {season} {activeYear}
                </div>
              </div>
              <button
                onClick={() => handleMonthNav(1)}
                style={{
                  background: T.bg, border: `1px solid ${T.border}`,
                  borderRadius: 8, width: 32, height: 32,
                  cursor: 'pointer', fontSize: 16, color: T.textMed,
                }}
              >›</button>
            </div>
          </div>

          {/* Progress block */}
          <div style={{ background: T.green50, borderRadius: 14, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.text }}>Progress</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: T.green700 }}>{pct}%</span>
            </div>
            <div style={{ height: 8, background: T.green100, borderRadius: 99 }}>
              <div style={{
                height: '100%', width: `${pct}%`,
                background: T.green700, borderRadius: 99, transition: 'width 0.4s',
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
              {[
                ['Done',  completedCount,             T.green700],
                ['Left',  totalTasks - completedCount, T.textLight],
                ['Total', totalTasks,                  T.textMed],
              ].map(([label, val, color]) => (
                <div key={label} style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color }}>{val}</div>
                  <div style={{ fontSize: 10, color: T.textLight }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Location card */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
              color: T.textLight, textTransform: 'uppercase', marginBottom: 8,
            }}>
              Location
            </div>
            <div style={{
              background: T.green50, border: `1px solid ${T.green200}`,
              borderRadius: 14, padding: '12px 14px',
              display: 'flex', flexDirection: 'column', gap: 5,
              fontSize: 12, color: T.textMed, lineHeight: 1.6,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>📍</span>
                <span style={{ fontWeight: 600, color: T.text }}>{settings.state}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>🌿</span>
                <span>USDA Zone <strong>{settings.hardinessZone}</strong></span>
              </div>
              {season && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>{seasonEmoji}</span>
                  <span style={{ color: T.green700, fontWeight: 600 }}>{season}</span>
                </div>
              )}
            </div>
          </div>

          {/* Category filter */}
          <div>
            <div style={{
              fontSize: 11, fontWeight: 700, letterSpacing: '0.08em',
              color: T.textLight, textTransform: 'uppercase', marginBottom: 10,
            }}>
              Filter by Category
            </div>
            {allCategories.map(cat => {
              const group  = taskGroups.find(g => g.title === cat);
              const color  = group ? group.color : T.green700;
              const active = filterCat === cat;
              return (
                <div
                  key={cat}
                  onClick={() => setFilterCat(cat)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 10px', borderRadius: 10, marginBottom: 4,
                    cursor: 'pointer',
                    background: active ? T.green50 : 'transparent',
                    border:     active ? `1px solid ${T.green200}` : '1px solid transparent',
                    transition: 'all 0.15s',
                  }}
                >
                  <div style={{ width: 8, height: 8, borderRadius: 3, background: color, flexShrink: 0 }} />
                  <span style={{
                    fontSize: 13, fontWeight: active ? 700 : 400,
                    color: active ? T.green700 : T.textMed,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {cat}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Main content ────────────────────────────────────────────────── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '24px 28px' }}>

          {/* Calendar view */}
          {activeNav === 'Calendar' && (
            <CalendarView
              monthName={monthName}
              activeYear={activeYear}
              monthData={monthData}
              completedTasks={completedTasks}
              onTaskToggle={onTaskToggle}
              onMonthChange={onMonthChange}
              monthStats={monthStats}
              isDesktop={true}
            />
          )}

          {/* History view */}
          {activeNav === 'History' && (
            <HistoryView
              completedTasks={completedTasks}
              zoneCustomizedData={zoneCustomizedData}
              activeYear={activeYear}
              isDesktop={true}
            />
          )}

          {/* Dashboard: Alert banner + task groups */}
          {activeNav === 'Dashboard' && (
          <>
          {/* Alert banner */}
          {zoneAlerts.length > 0 && (
            <div style={{
              background: T.amberLight, border: `1px solid #f0c080`,
              borderRadius: 14, padding: '14px 18px',
              marginBottom: 20, display: 'flex', alignItems: 'flex-start', gap: 12,
            }}>
              <span style={{ fontSize: 20, flexShrink: 0, marginTop: 1 }}>⚠️</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.amber }}>
                  {zoneAlerts[0].title || 'Zone Alert'}
                </div>
                <div style={{ fontSize: 13, color: '#8a5a1a', marginTop: 3 }}>
                  {zoneAlerts[0].message || ''}
                </div>
              </div>
              <button
                onClick={() => onDismissAlert && onDismissAlert(zoneAlerts[0].title)}
                style={{
                  background: 'none', border: 'none',
                  cursor: 'pointer', color: '#c09060', fontSize: 18, flexShrink: 0,
                }}
              >✕</button>
            </div>
          )}

          {/* Task groups */}
          {visibleGroups.map(group => {
            const catDone = group.tasks.filter(t => t.done).length;
            return (
              <div key={group.title} style={{ marginBottom: 24 }}>
                {/* Category header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <div style={{
                    width: 14, height: 14, borderRadius: 4,
                    background: group.color, flexShrink: 0,
                  }} />
                  <span style={{ fontSize: 16, fontWeight: 700, color: T.text }}>{group.title}</span>
                  <span style={{ fontSize: 13, color: T.textLight }}>
                    — {catDone}/{group.tasks.length} complete
                  </span>
                  {/* Dot progress */}
                  <div style={{ display: 'flex', gap: 4, marginLeft: 4 }}>
                    {group.tasks.map(t => (
                      <div key={t.id} style={{
                        width: 8, height: 8, borderRadius: 99,
                        background: t.done ? group.color : T.border,
                      }} />
                    ))}
                  </div>
                </div>

                {/* Task cards */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {group.tasks.map(task => (
                    <div
                      key={task.id}
                      style={{
                        background: T.white, borderRadius: 14,
                        padding: '14px 18px',
                        display: 'flex', alignItems: 'center', gap: 14,
                        boxShadow: `0 1px 4px ${T.shadow}`,
                        opacity: task.done ? 0.55 : 1,
                        transition: 'opacity 0.2s',
                        borderLeft: `4px solid ${group.color}`,
                      }}
                    >
                      <Checkbox checked={task.done} onChange={() => onTaskToggle(task.id)} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 14, fontWeight: 600, color: T.text, lineHeight: 1.3,
                          textDecoration: task.done ? 'line-through' : 'none',
                        }}>
                          {task.title}
                        </div>
                      </div>
                      <PriorityPill priority={task.priority} />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Notes */}
          <NotesSection monthName={monthName} activeYear={activeYear} isDesktop={true} />
          </>
          )}
        </div>

        {/* ── Right products panel ────────────────────────────────────────── */}
        <div style={{
          width: 240, background: T.white, borderLeft: `1px solid ${T.border}`,
          padding: '24px 18px', overflowY: 'auto', flexShrink: 0,
        }}>
          <div style={{
            fontSize: 12, fontWeight: 700, letterSpacing: '0.08em',
            color: T.textLight, textTransform: 'uppercase', marginBottom: 8,
          }}>
            Helpful for {monthName}
          </div>
          <div style={{ fontSize: 11, color: T.textLight, marginBottom: 16, lineHeight: 1.5 }}>
            Products matched to your active tasks this month
          </div>

          {recommendedProducts.map((product, i) => (
            <div
              key={product.asin}
              style={{
                marginBottom: 16, paddingBottom: 16,
                borderBottom: i < recommendedProducts.length - 1 ? `1px solid ${T.border}` : 'none',
              }}
            >
              <ProductCard product={product} />
            </div>
          ))}

          <div style={{ fontSize: 10, color: T.textLight, lineHeight: 1.5, marginTop: 8 }}>
            Amazon Associate. We may earn a commission from purchases at no extra cost to you.
          </div>
        </div>

      </div>
    </div>
  );
};

export default DesktopDashboard;
